import { memo, ReactNode } from "react";
import { Typography, Checkbox, Grid2, Box, useTheme } from "@mui/material";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { alpha } from "@mui/material/styles";
import { icon } from "@/app/style/icons";
import "flag-icons/css/flag-icons.min.css";
import DocumentTooltip from "@chat/components/sidebar/documents/DocumentTooltip";

export interface CustomTreeItemProps {
    nodeId: string;
    nodeLabel: string;
    nodeJurisdiction?: string;
    tooltipLabel?: string;
    hoverTitle?: string;
    isMainContent?: boolean;
    isAmendment?: boolean;
    isCategory?: boolean;
    isGraphEnabled?: boolean;
    lexiconMode?: boolean;
    mainAvailable?: boolean;
    mainSelected?: boolean;
    summarySelected?: boolean;
    disableTooltip?: boolean;
    documentNotActive?: boolean;
    mainOnClick?: () => void;
    onToggleMain: () => void;
    children?: ReactNode;
    hasChildren?: boolean; // New prop to indicate if item is expandable
}

// Hilfsfunktion, um den Ländercode für die Flagge zu ermitteln
export const getCountyCode = (jurisdiction?: string) => {
    if (jurisdiction === "EU") return "eu";
    // Handle both "DE" (current standard) and "GER" (legacy/metadata values)
    if (jurisdiction === "DE" || jurisdiction === "GER") return "de";
    if (jurisdiction === "OTHER") return "un"; // uses United-Nations flag (fi-un)
    return "";
};

const CustomTreeItem = memo(
    ({
        nodeId,
        nodeLabel,
        nodeJurisdiction = "NONE",
        mainAvailable = false,
        mainSelected = false,
        summarySelected = false,
        isMainContent = false,
        isAmendment = false,
        isCategory = false,
        isGraphEnabled = false,
        lexiconMode = false,
        disableTooltip = false,
        documentNotActive = false,
        mainOnClick,
        onToggleMain,
        hoverTitle = "",
        tooltipLabel = "",
        children,
        hasChildren = false,
    }: CustomTreeItemProps) => {
        const theme = useTheme();

        // Add document not active text to tooltip
        let tooltipText = tooltipLabel || "";
        if (documentNotActive) {
            tooltipText = `${tooltipText} (nicht aktiv für dieses Unternehmen)`;
        }

        const labelContent = (
            <DocumentTooltip title={tooltipText} hoverTitle={hoverTitle} placement={"right"} disabled={disableTooltip}>
                <Grid2
                    container
                    size={12}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        p: isMainContent ? 0.25 : 0,
                        pl: 1,
                        pr: 1,
                        borderRadius: 1,
                        borderColor: mainSelected ? theme.palette.semantic.button.primary : "transparent",
                        bgcolor: mainSelected ? alpha(theme.palette.semantic.button.primary, 0.2) : "transparent",
                        "&:hover": {
                            bgcolor: mainSelected ? alpha(theme.palette.semantic.button.primary, 0.15) : alpha(theme.palette.semantic.button.primary, 0.1),
                        },
                        transition: "all 0.2s ease",
                        opacity: documentNotActive ? 0.5 : 1,
                        textDecoration: documentNotActive ? "line-through" : "none",
                    }}
                >
                    <Grid2 container alignItems="center" flexGrow={1} gap={1}>
                        {/* Left-side: display folder icon if not a document */}
                        {(isCategory || !isMainContent) && (
                            <Grid2>
                                {isCategory ? (
                                    <icon.folder
                                        sx={{
                                            fontSize: "1.2rem",
                                            color: theme.palette.semantic.button.primary,
                                            opacity: 0.7,
                                            mb: -0.5,
                                        }}
                                    />
                                ) : isAmendment ? (
                                    <icon.amendment
                                        sx={{
                                            fontSize: "1.2rem",
                                            color: theme.palette.semantic.button.primary,
                                            opacity: 0.7,
                                            mb: -0.5,
                                        }}
                                    />
                                ) : (
                                    <icon.page
                                        sx={{
                                            fontSize: "1.2rem",
                                            color: theme.palette.semantic.button.primary,
                                            opacity: 0.7,
                                            mb: -0.5,
                                        }}
                                    />
                                )}
                            </Grid2>
                        )}

                        {/* Label text */}
                        <Grid2 size="grow">
                            <Typography
                                variant="body2"
                                sx={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    fontStyle: isAmendment ? "italic" : "normal",
                                }}
                            >
                                {nodeLabel}
                            </Typography>
                        </Grid2>

                        {/* For all documents, display a Chip and checkboxes */}
                        <>
                            {!isCategory && (
                                <>
                                    {/*{isMainContent && <Box className={`fi fi-${getCountyCode(nodeJurisdiction)}`} sx={{ opacity: 0.5, borderRadius: "2px" }} />}*/}
                                    <Box className={`fi fi-${getCountyCode(nodeJurisdiction)}`} sx={{ opacity: 0.5, borderRadius: "2px" }} />

                                    <Grid2 container>
                                        {/* Main content checkbox - always rendered for layout consistency */}
                                        <Checkbox
                                            checked={isGraphEnabled ? true : mainSelected}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                if (mainAvailable) onToggleMain();
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            size="small"
                                            color="secondary"
                                            disabled={!mainAvailable || summarySelected || isGraphEnabled}
                                            sx={{
                                                px: 0.75,
                                                py: 0.5,
                                                visibility: mainAvailable ? "visible" : "hidden",
                                            }}
                                        />
                                    </Grid2>
                                </>
                            )}
                        </>
                    </Grid2>
                </Grid2>
            </DocumentTooltip>
        );

        return (
            <TreeItem
                itemId={nodeId}
                label={labelContent}
                onClick={() => {
                    // Enable row-level clicking in lexicon mode for all items
                    // In chat mode, only allow row-level click for items without children (original behavior)
                    if (mainOnClick && (lexiconMode || (mainAvailable && !hasChildren))) {
                        mainOnClick();
                    }
                }}
            >
                {children}
            </TreeItem>
        );
    },
);

export default CustomTreeItem;
